import { cx } from '@/utils/cx'

/** ADR 0030: the three visible classes of work. Stored on every item (`work_item.work_class`); never derived here. */
export type WorkClass = 'close' | 'recurring' | 'task'

export const WORK_CLASS_LABEL: Record<WorkClass, string> = { close: 'Close', recurring: 'Recurring', task: 'Task' }
/** Long form for the accessible name, so a screen reader hears what the color says. */
export const WORK_CLASS_DESCRIPTION: Record<WorkClass, string> = {
  close: 'Close checklist item: stamped from the template for a period, counts toward the period close',
  recurring: 'Recurring item: stamped for a day (daily, weekly or biweekly), outside the period close',
  task: 'Task: added by a person, any due date',
}

export function isWorkClass(v: unknown): v is WorkClass {
  return v === 'close' || v === 'recurring' || v === 'task'
}

/**
 * One chip per class, same shape as StatusPill, its own color token pair (gallop.css --shell-class-*). Shown on
 * every list that can mix classes (My work, the client Close tab, the file Close tab, the Recurring block) and in
 * the drawer header, so a Controller never has to infer from the row's position what kind of work it is (QA-32).
 */
export function WorkClassChip({ workClass, className, size = 'xs' }: { workClass: WorkClass | string | null | undefined, className?: string, size?: 'xs' | 'sm' }) {
  if (!isWorkClass(workClass))
    return null
  return (
    <span
      className={cx('inline-flex shrink-0 items-center rounded font-medium whitespace-nowrap', size === 'xs' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs', className)}
      style={{ background: `var(--shell-class-${workClass}-bg)`, color: `var(--shell-class-${workClass}-fg)` }}
      aria-label={WORK_CLASS_DESCRIPTION[workClass]}
      title={WORK_CLASS_DESCRIPTION[workClass]}
      data-testid="work-class-chip"
      data-class={workClass}
    >
      {WORK_CLASS_LABEL[workClass]}
    </span>
  )
}
